import { MemberRepository, Member, KeyHolder, SortMethodStore, SortMethod } from './MemberRepository';

export interface KeyHolderState {
  keyHolders: KeyHolder[];
  canUndo: boolean;
  canRedo: boolean
}

export interface MemberState {
  memberInOffice: Member[];
  memberOutOffice: Member[];
  memberSortMethod: SortMethod;
}

export default class KMSUsecase {
  // サービスクラス
  private readonly repo: MemberRepository;
  private readonly memberSortMethodStore: SortMethodStore;
  // インスタンス変数
  private keyholders: KeyHolder[];
  private members: MembersPresenter;
  private readonly keyholderHistory: KeyholderHistory;

  constructor() {
    this.repo = new MemberRepository();
    this.memberSortMethodStore = new SortMethodStore();

    this.keyholders = [];
    this.members = new MembersPresenter([]);
    this.keyholderHistory = new KeyholderHistory();
  }

  public getKeyHolders(): Promise<KeyHolderState> {
    const promise = this.repo.fetchHolder().then(keyfolders => {
      this.keyholders = keyfolders;
      this.keyholderHistory.push(keyfolders);
      return {
        keyHolders: keyfolders,
        canUndo: this.keyholderHistory.canUndo,
        canRedo: this.keyholderHistory.canRedo,
      }
    });
    return promise;
  }

  public getMembers(): Promise<MemberState> {
    return this.repo.fetchAll().then(members => {
      this.members = new MembersPresenter(members);
      const sortMethod = this.memberSortMethodStore.method || SortMethod.ID_ASC;
      return this.members.getOrderBy(sortMethod);
    })
  }

  public bindKeyHolder(member: Member): KeyHolderState {
    // キー保持者が3人いる場合は無視
    if (this.keyholders.length >= 3) {
      return;
    }
    // キー保持者の重複は禁止
    const findSameId = (h: KeyHolder) => h.member.id === member.id;
    if (this.keyholders.find(findSameId)) {
      return;
    }
    // キー保持者更新
    const newKeyHolders = this.keyholders = this.keyholders.concat([{member: member, since: new Date()}])

    // データ保存
    this.repo.storeHolder(newKeyHolders);
    // 履歴保存
    this.keyholderHistory.push(newKeyHolders);

    return {
      keyHolders: newKeyHolders,
      canUndo: this.keyholderHistory.canUndo,
      canRedo: this.keyholderHistory.canRedo,
    };
  }

  public unbindKeyHolder(member: Member): KeyHolderState {
    // 選択されたメンバーを保持者から取り除く
    const newKeyHolders = this.keyholders = this.keyholders.filter((holder) => {
      return holder.member.id !== member.id;
    });

    // データ保存
    this.repo.storeHolder(newKeyHolders);
    // 履歴保存
    this.keyholderHistory.push(newKeyHolders);

    return {
      keyHolders: newKeyHolders,
      canUndo: this.keyholderHistory.canUndo,
      canRedo: this.keyholderHistory.canRedo,
    };
  }

  public undoKeyHolders(): KeyHolderState {
    const newKeyholders = this.keyholders = this.keyholderHistory.previous;
    this.repo.storeHolder(newKeyholders);
    return {
      keyHolders: newKeyholders,
      canUndo: this.keyholderHistory.canUndo,
      canRedo: this.keyholderHistory.canRedo,
    }
  }

  public redoKeyHolders(): KeyHolderState {
    const newKeyholders = this.keyholders = this.keyholderHistory.forward;
    this.repo.storeHolder(newKeyholders);
    return {
      keyHolders: newKeyholders,
      canUndo: this.keyholderHistory.canUndo,
      canRedo: this.keyholderHistory.canRedo,
    }
  }

  public changeMembersSort(sortMethod: SortMethod): MemberState {
    this.memberSortMethodStore.method = sortMethod;
    return this.members.getOrderBy(sortMethod);
  }
}

class KeyholderHistory {
  private history: KeyHolder[][];
  private currentPos: number; 

  constructor() {
    this.history = [];
    this.currentPos = 0;
  }

  public push(keyholders: KeyHolder[]) {
    this.history = this.history.slice(this.currentPos);
    this.history.unshift(keyholders);
    this.currentPos = 0;
  }

  public get previous(): KeyHolder[] {
    return this.history[++this.currentPos];
  }

  public get forward(): KeyHolder[] {
    return this.history[--this.currentPos];
  }

  public get canUndo(): boolean {
    return this.history.length > (this.currentPos + 1);
  }

  public get canRedo(): boolean {
    return this.currentPos !== 0;
  }
}

interface ICompare {
  (a: Member, b: Member): number
}
class MembersPresenter {
  constructor(private members: Member[]) {}

  public getOrderBy(sortMethod: SortMethod): MemberState {
      const [memberInOffice, memberOutOffice] = this.splitMembers();
      const compareFunc = this.choiceCompareFunc(sortMethod);
    
      return {
        memberInOffice: memberInOffice.sort(compareFunc),
        memberOutOffice: memberOutOffice.sort(compareFunc),
        memberSortMethod: sortMethod,
      }
  }

  private splitMembers(): [Member[], Member[]] {
    return [
      this.members.filter((m) => m.inOffice),
      this.members.filter((m) => !m.inOffice),
    ]
  }
  
  private choiceCompareFunc(method: SortMethod): ICompare {
    if (method === SortMethod.YOMI_ASC) {
      return (a: Member, b: Member) => {
        if (a.yomi > b.yomi) return 1;
        if (a.yomi < b.yomi) return -1;
        return 0;
      }
    } else if (method === SortMethod.ID_ASC) {
      return (a: Member, b: Member) => {
        if (a.id > b.id) return 1;
        if (a.id < b.id) return -1;
        return 0;
      }
    }
  
    // default
    return (a: Member, b: Member) => { return a > b ? 1 : (a < b ? -1 : 0); }
  }
}
